import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api, { feedbackFormsAPI, feedbackTemplatesAPI, settingsAPI } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/dateUtils';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, User, Building2, Calendar, ClipboardCheck, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { downloadBlobResponse } from '../components/FeedbackReportDialog';

const FeedbackFormView = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [form, setForm] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
    fetchLogo();
  }, [formId]);

  // Auto-trigger PDF export when navigated from student profile with ?download=pdf
  useEffect(() => {
    if (searchParams.get('download') === 'pdf' && !loading && submission && form) {
      exportPdf();
    }
  }, [loading, submission, form]);

  const fetchLogo = async () => {
    try {
      const res = await settingsAPI.get();
      if (res.data?.logo_url) setLogoUrl(res.data.logo_url);
    } catch (e) { /* ignore */ }
  };

  const fetchData = async () => {
    try {
      const subRes = await feedbackFormsAPI.getSubmission(formId);
      setSubmission(subRes.data);

      const formsRes = await feedbackFormsAPI.getAll({});
      const foundForm = formsRes.data.find(f => f.id === formId);
      setForm(foundForm);

      if (foundForm?.template_id) {
        try {
          const tmplRes = await feedbackTemplatesAPI.getOne(foundForm.template_id);
          setTemplate(tmplRes.data);
        } catch {
          // Template was deleted — use snapshot stored in the form
          if (foundForm.template_snapshot) {
            setTemplate(foundForm.template_snapshot);
          }
        }
      } else if (foundForm?.template_snapshot) {
        setTemplate(foundForm.template_snapshot);
      }
    } catch (error) {
      toast.error('Failed to load submission');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 7) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (score >= 5) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-red-100 text-red-700 border-red-200';
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const studentName = (form?.student_name || 'Student').replace(/\s+/g, '_');
      const restaurant = (form?.restaurant_name || 'Restaurant').replace(/\s+/g, '_');
      const date = submission?.submitted_at
        ? new Date(submission.submitted_at).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];
      // Rendered server-side (WeasyPrint) so text and badges are positioned exactly.
      const res = await api.get(`/feedback-reports/form/${formId}/pdf`, { responseType: 'blob' });
      downloadBlobResponse(res, `${studentName}_${restaurant}_${date}.pdf`);
      toast.success('PDF exported successfully');
    } catch (err) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  if (!submission) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">No submission found for this form.</p>
          <Button variant="ghost" onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </Layout>
    );
  }

  const assessmentScale = template?.assessment_scale || form?.template_snapshot?.assessment_scale;

  return (
    <Layout>
      <div data-testid="feedback-view-page" className="space-y-6 animate-fade-in max-w-4xl">
        {/* Action Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-ica-navy">Feedback Submission</h1>
              <p className="text-gray-500 text-sm">
                Submitted {submission.submitted_at ? formatDateTime(submission.submitted_at) : 'N/A'}
              </p>
            </div>
          </div>
          <Button
            data-testid="export-pdf-btn"
            onClick={exportPdf}
            disabled={exporting}
            className="bg-ica-navy hover:bg-[#152a47] text-white"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
        </div>

        <div>
          {/* Form Info with Logo */}
          {form && (
            <Card className="mb-4">
              <CardContent className="pt-6">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-xl font-bold text-[#1e3a5f]">
                    {template?.name || form.template_snapshot?.name || 'Assessment'}
                  </h2>
                  {logoUrl && (
                    <img src={logoUrl} alt="School Logo" className="h-14 w-auto object-contain ml-4 flex-shrink-0" />
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-gray-400 text-xs">Student</p>
                      <p className="font-medium">{form.student_name}</p>
                      <p className="text-xs text-gray-500">{form.student_number}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-gray-400 text-xs">Restaurant</p>
                      <p className="font-medium">{form.restaurant_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <ClipboardCheck className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-gray-400 text-xs">Assessment #</p>
                      <p className="font-medium">{form.assessment_number}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-gray-400 text-xs">Chef / Mentor</p>
                      <p className="font-medium">{submission.chef_name}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Assessment Scale */}
          {assessmentScale && (
            <Card className="mb-4">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-[#1e3a5f] mb-3 text-sm">Assessment Scale</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-8 text-sm">
                  {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(score => (
                    <div key={score} className="flex items-start gap-2">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                        score >= 7 ? 'bg-emerald-100 text-emerald-700' :
                        score >= 5 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {score}
                      </span>
                      <span className="text-gray-600 leading-snug pt-0.5">{assessmentScale[score.toString()] || ''}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ratings */}
          {template && (
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-lg">Assessment Ratings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {template.elements.map((element) => {
                  if (element.type === 'section_header') {
                    return (
                      <h3 key={element.id} className="text-base font-semibold text-ica-navy border-b pb-2 mt-4">
                        {element.label}
                      </h3>
                    );
                  }

                  const value = submission.responses?.[element.id];
                  const comment = submission.responses?.[`${element.id}_comment`];

                  return (
                    <div key={element.id} className="p-3 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">{element.label}</p>
                          {element.module && (
                            <span className="text-xs text-[#bc953f]">({element.module})</span>
                          )}
                          {element.description && (
                            <p className="text-sm text-gray-500 mt-1">{element.description}</p>
                          )}
                        </div>
                        {element.type === 'rating' && value !== undefined && (
                          <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getScoreColor(value)}`}>
                            {value}/10
                          </span>
                        )}
                      </div>
                      {element.type !== 'rating' && value && (
                        <p className="mt-2 text-gray-700 bg-gray-50 p-2 rounded text-sm">{value}</p>
                      )}
                      {comment && (
                        <p className="mt-2 text-sm text-gray-600 italic bg-gray-50 p-2 rounded">
                          Comment: {comment}
                        </p>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Written Feedback */}
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-lg">Written Feedback</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {submission.strengths && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Strengths</p>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{submission.strengths}</p>
                </div>
              )}
              {submission.weaknesses && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Weaknesses</p>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{submission.weaknesses}</p>
                </div>
              )}
              {submission.training_feedback && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">ICA Training Feedback</p>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{submission.training_feedback}</p>
                </div>
              )}
              {submission.general_comments && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">General Comments</p>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{submission.general_comments}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Signature */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Signature</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Chef / Mentor</p>
                  <p className="font-medium text-gray-800">{submission.chef_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Signature</p>
                  {submission.signature_type === 'drawn' && submission.chef_signature?.startsWith('data:') ? (
                    <img
                      src={submission.chef_signature}
                      alt="Signature"
                      className="max-h-20 border rounded p-1"
                    />
                  ) : (
                    <p className="text-xl italic text-[#1e3a5f]" style={{ fontFamily: 'cursive' }}>
                      {submission.chef_signature}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Date</p>
                  <p className="font-medium text-gray-800">
                    {submission.submitted_at ? formatDate(submission.submitted_at) : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default FeedbackFormView;
